import { DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    computed,
    effect,
    input,
    output,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { UiThumbnailContent } from '@features/media-library/interfaces';
import { ThumbnailItemComponent } from '@ntv360/component-pantry';
import { ActionsComponent } from './actions/actions.component';

import { ThumbnailSkeletonComponent } from '../thumbnail-skeleton/thumbnail-skeleton.component';

/**
 * A responsive, drag-and-drop enabled grid component for displaying playlist
 * contents and blocks. Supports reordering via CDK drag-and-drop, adaptive
 * column spanning for channel items, and inline action popovers.
 */
@Component({
    selector: 'app-playlist-grid',
    standalone: true,
    imports: [DragDropModule, ThumbnailItemComponent, ActionsComponent, ThumbnailSkeletonComponent],
    templateUrl: './playlist-grid.component.html',
    styleUrl: './playlist-grid.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlaylistGridComponent {
    /** Whether the grid is currently loading its contents */
    readonly isLoading = input<boolean>(false);

    /** Input list of individual content items to display in the grid */
    readonly playlistContents = input<any[]>([]);

    /** Input list of block items (grouped content) to display in the grid */
    readonly playlistBlocks = input<any[]>([]);

    /** Controls whether to enable removing or deleting items  */
    public showRemoveControl = input<boolean>(false);

    /** Controls whether the per-item actions ellipsis popover is rendered */
    public showActions = input<boolean>(false);

    /** Emits the metadata of the item or block to be removed */
    readonly remove = output<any>();

    /** Emits the updated list of items after a reorder operation */
    readonly reorder = output<any[]>();

    /** Sequential list of items (contents and blocks combined) */
    readonly playlistItems = signal<any[]>([]);

    /** Reference to the grid container DOM element for resize observation */
    readonly gridContainer = viewChild<ElementRef>('gridContainer');

    /** Computed number of columns based on the current container width */
    readonly columnCount = signal<number>(0);

    /** Whether a drag operation is currently in progress */
    private _isDragging = false;

    /** Logical index of the item currently being dragged */
    private _dragSourceLogicalIndex: number | null = null;

    /** Logical index of the current drop target position */
    private _dropTargetLogicalIndex: number | null = null;

    /** Reactive signal exposing the drop target index for template binding */
    readonly dropTargetLogicalIndex = signal<number | null>(null);

    /** Reactive signal exposing the drag source index for template binding */
    readonly dragSourceLogicalIndex = signal<number | null>(null);

    /**
     * Computed signal that produces the final grouped UI data for rendering.
     * Re-evaluates whenever `playlistItems` or `columnCount` changes.
     */
    readonly groupedData = computed(() => {
        const items = this.playlistItems();
        const cols = this.columnCount();
        return this.groupData(items, cols);
    });

    constructor() {
        // Sync local playlistItems signal whenever inputs change
        effect(() => {
            const contents = this.playlistContents() || [];
            const blocks = this.playlistBlocks() || [];

            untracked(() => {
                this.playlistItems.set([
                    ...contents.map((c: any) => ({ type: 'content', data: c })),
                    ...blocks.map((b: any) => ({ type: 'block', data: b })),
                ]);
            });
        });

        /**
         * Sets up a ResizeObserver on the grid container to dynamically
         * calculate the column count based on available width and gap.
         */
        effect((onCleanup) => {
            const isLoading = this.isLoading();
            const gridContainer = this.gridContainer();

            if (!isLoading && gridContainer) {
                const container = gridContainer.nativeElement;

                const observer = new ResizeObserver(() => {
                    const style = window.getComputedStyle(container);
                    const containerWidth = container.clientWidth;
                    const paddingLeft = parseFloat(style.paddingLeft) || 0;
                    const paddingRight = parseFloat(style.paddingRight) || 0;
                    const gap = parseFloat(style.gap) || 0;

                    // Matches SCSS: minmax(190px, 1fr)
                    const minColWidth = 190;

                    const availableWidth = containerWidth - paddingLeft - paddingRight;

                    // Calculation: availableWidth = n * minColWidth + (n - 1) * gap
                    // n = (availableWidth + gap) / (minColWidth + gap)
                    const cols = Math.floor((availableWidth + gap) / (minColWidth + gap));
                    const safeCols = Math.max(1, cols);

                    untracked(() => {
                        if (this.columnCount() !== safeCols) {
                            this.columnCount.set(safeCols);
                        }
                    });
                });

                observer.observe(container);
                onCleanup(() => observer.disconnect());
            }
        });
    }

    /**
     * Groups playlist items into row-aware layout entries with column spanning.
     * Handles both standalone content items and multi-item blocks, splitting
     * blocks across rows when they overflow the available columns.
     *
     * @param items The flat list of playlist items (contents and blocks)
     * @param totalCols The total number of grid columns available
     * @returns An array of grouped layout entries ready for template rendering
     */
    private groupData(items: any[], totalCols: number) {
        if (!totalCols || totalCols < 1) return [];

        const result: any[] = [];
        let trackCounter = 1;

        const getAdaptiveSpan = (item: any, currentTrack: number, maxCols: number) => {
            if (item.fileType !== 'channel') return 1;
            const remainingSlots = maxCols - currentTrack + 1;
            return remainingSlots === 1 ? 1 : 2;
        };

        items.forEach((itemWrapper, logicalIndex) => {
            if (itemWrapper.type === 'content') {
                const content = itemWrapper.data;
                let span = getAdaptiveSpan(content, trackCounter, totalCols);
                if (trackCounter + span - 1 > totalCols) {
                    trackCounter = 1;
                    span = getAdaptiveSpan(content, trackCounter, totalCols);
                }

                const uiItem: UiThumbnailContent = {
                    ...content,
                    id: content.id || content.contentId || content.title,
                    name: content.name || content.title || content.fileName,
                    type: content.type || content.fileType,
                    src: content.src || content.url,
                } as UiThumbnailContent;

                result.push({
                    type: 'content',
                    logicalIndex,
                    data: uiItem,
                    span,
                });
                trackCounter += span;
                if (trackCounter > totalCols) trackCounter = 1;
            } else if (itemWrapper.type === 'block') {
                const block = itemWrapper.data;

                let groupItems: any[] = [];
                let blockSpanTotal = 0;
                let isFirstPart = true;

                let initialItem = block.contents[0];
                if (initialItem) {
                    let firstSpan = getAdaptiveSpan(initialItem, trackCounter, totalCols);
                    if (trackCounter + firstSpan - 1 > totalCols) trackCounter = 1;
                }

                let startTrack = trackCounter;

                const blockColors: Record<string, string> = {
                    'As is': '#EF9F27',
                    'Cafe Block': '#22C55E',
                    'Space Block': '#EF9F27',
                    '_Block^Channel': '#EF9F27',
                    '^Block_Channel': '#22C55E',
                };
                const blockColor = blockColors[block.name] || '#EF9F27';

                block.contents.forEach((subItem: any, subIndex: number) => {
                    let itemSpan = getAdaptiveSpan(subItem, trackCounter, totalCols);
                    if (itemSpan > totalCols) itemSpan = totalCols;

                    if (trackCounter + itemSpan - 1 <= totalCols) {
                        const uiSubItem: UiThumbnailContent = {
                            ...subItem,
                            id: subItem.id || subItem.contentId || subItem.title,
                            name: subItem.name || subItem.title || subItem.fileName,
                            type: subItem.type || subItem.fileType,
                            src: subItem.src || subItem.url || subItem.thumbnail,
                            duration: subItem.duration,
                            isChannel: (subItem.type || subItem.fileType) === 'channel',
                        } as UiThumbnailContent;

                        groupItems.push({
                            ...uiSubItem,
                            span: itemSpan,
                        });
                        blockSpanTotal += itemSpan;
                        trackCounter += itemSpan;
                    } else {
                        result.push({
                            type: 'block',
                            logicalIndex,
                            name: block.name,
                            description: block.description,
                            color: blockColor,
                            items: [...groupItems],
                            start: startTrack,
                            totalSpan: blockSpanTotal,
                            isWrapped: true,
                            partType: isFirstPart ? 'first' : 'middle',
                        });
                        isFirstPart = false;
                        trackCounter = 1;
                        startTrack = 1;
                        itemSpan = getAdaptiveSpan(subItem, trackCounter, totalCols);
                        const uiSubItem: UiThumbnailContent = {
                            ...subItem,
                            id: subItem.id || subItem.contentId || subItem.title,
                            name: subItem.name || subItem.title || subItem.fileName,
                            type: subItem.type || subItem.fileType,
                            src: subItem.src || subItem.url || subItem.thumbnail,
                            duration: subItem.duration,
                        } as UiThumbnailContent;

                        groupItems = [
                            {
                                ...uiSubItem,
                                span: itemSpan,
                            },
                        ];
                        blockSpanTotal = itemSpan;
                        trackCounter += itemSpan;
                    }

                    if (subIndex === block.contents.length - 1) {
                        result.push({
                            type: 'block',
                            logicalIndex,
                            name: block.name,
                            description: block.description,
                            color: blockColor,
                            items: groupItems,
                            start: startTrack,
                            totalSpan: blockSpanTotal,
                            isWrapped: !isFirstPart,
                            partType: isFirstPart ? 'single' : 'last',
                            isChannel: subItem.fileType === 'channel',
                        });
                    }
                });
                if (trackCounter > totalCols) trackCounter = 1;
            }
        });

        return result;
    }

    /**
     * Called when a drag operation begins on a grid item.
     * Captures the source index and sets the dragging state.
     *
     * @param logicalIndex The logical index of the item being dragged
     */
    onDragStarted(logicalIndex: number) {
        this._isDragging = true;
        this._dragSourceLogicalIndex = logicalIndex;
        this.dragSourceLogicalIndex.set(logicalIndex);
    }

    /**
     * Called when a drag operation ends. Performs the re-order if
     * the source and target indices are valid and different.
     */
    onDragEnded() {
        if (this._rafId !== null) {
            cancelAnimationFrame(this._rafId);
            this._rafId = null;
        }

        const fromLogicalIndex = this._dragSourceLogicalIndex;
        const toLogicalIndex = this._dropTargetLogicalIndex;

        // Clean up all state first
        this._clearDragState();

        // Perform re-order if we have valid source and target
        if (fromLogicalIndex === null || toLogicalIndex === null) return;
        if (fromLogicalIndex === toLogicalIndex) return;

        const items = [...this.playlistItems()];
        moveItemInArray(items, fromLogicalIndex, toLogicalIndex);
        this.playlistItems.set(items);

        // Emit the updated unwrapped data
        const unwrapped = items.filter((i) => i.type === 'content').map((i) => i.data);
        this.reorder.emit(unwrapped);
    }

    /** ID of the pending requestAnimationFrame callback, used to throttle pointer tracking */
    private _rafId: number | null = null;

    /**
     * Throttled pointer move handler for the grid container.
     * Updates the drop target indicator via requestAnimationFrame.
     *
     * @param event The pointer move event from the grid container
     */
    onGridPointerMove(event: PointerEvent) {
        if (!this._isDragging) return;

        if (this._rafId !== null) return; // skip if frame already queued

        this._rafId = requestAnimationFrame(() => {
            this._rafId = null;
            this._updateDropTarget(event.clientX, event.clientY);
        });
    }

    /**
     * Determines which grid item is under the given coordinates
     * and updates the drop target index accordingly.
     *
     * @param x The client X coordinate
     * @param y The client Y coordinate
     */
    private _updateDropTarget(x: number, y: number) {
        const el = document.elementFromPoint(x, y);
        if (!el) {
            this._dropTargetLogicalIndex = null;
            this.dropTargetLogicalIndex.set(null);
            return;
        }

        const dragEl = el.closest('[data-logical-index]');
        if (!dragEl) {
            this._dropTargetLogicalIndex = null;
            this.dropTargetLogicalIndex.set(null);
            return;
        }

        const logicalIndex = Number(dragEl.getAttribute('data-logical-index'));
        if (logicalIndex === this._dragSourceLogicalIndex) {
            this._dropTargetLogicalIndex = null;
            this.dropTargetLogicalIndex.set(null);
            return;
        }

        if (logicalIndex !== this._dropTargetLogicalIndex) {
            this._dropTargetLogicalIndex = logicalIndex;
            this.dropTargetLogicalIndex.set(logicalIndex);
        }
    }

    /**
     * Resets all drag-and-drop tracking state back to idle.
     */
    private _clearDragState() {
        this._isDragging = false;
        this._dragSourceLogicalIndex = null;
        this._dropTargetLogicalIndex = null;
        this.dragSourceLogicalIndex.set(null);
        this.dropTargetLogicalIndex.set(null);
    }

    /** Tracks the logical index of the currently hovered grid item */
    protected hoveredLogicalIndex = signal<number | null>(null);

    /** Tracks the logical index of the item whose action popover is open */
    protected activePopoverIndex = signal<number | null>(null);

    /**
     * Handles the visibility change of the popover
     * @param visible Whether the popover is visible
     * @param index The logical index of the item
     */
    protected onPopoverVisibilityChange(visible: boolean, index: number): void {
        if (visible) {
            this.activePopoverIndex.set(index);
        } else {
            this.activePopoverIndex.set(null);
        }
    }

    /** Handles setting the hovered state to true on mouse enter over the subgrid */
    onMouseEnter(logicalIndex: number) {
        this.hoveredLogicalIndex.set(logicalIndex);
    }

    /** Handles setting the hovered state to false on mouse leave over the subgrid */
    onMouseLeave() {
        this.hoveredLogicalIndex.set(null);
    }

    /**
     * Emits the removal event for an item or block
     * @param event The mouse event
     * @param data The item or block metadata
     */
    protected onRemove(event: Event, data: any): void {
        event.stopPropagation();
        this.remove.emit(data);
    }
}
